import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Copy, ExternalLink, Trash2, Edit2, Plus, X, LogOut, Loader2, Check, Upload, MessageCircle, MousePointerClick, KeyRound, AlertTriangle, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EMPTY_FORM = {
  business_name: '',
  bot_slug: '',
  address: '',
  schedule: '',
  services: '',
  wa_number: '',
  logo_url: '',
  primary_color: '#1a1a2e',
  secondary_color: '#6366f1',
  text_color: '#ffffff',
  welcome_message: '¡Hola! ¿En qué te puedo ayudar?',
  team_contacts: [],
};

// Cap mirrored from backend (`MAX_TEAM_CONTACTS` in routes/partner.py).
const MAX_TEAM_CONTACTS = 8;

function validateTeamContact(c) {
  /* Returns {name, wa_number, specialty} error map. Empty values mean OK. */
  const errs = {};
  const name = (c?.name || '').trim();
  const wa = (c?.wa_number || '').replace(/\D/g, '');
  const sp = (c?.specialty || '').trim();
  if (name.length < 1 || name.length > 50) errs.name = 'Entre 1 y 50 caracteres.';
  if (wa.length < 10 || wa.length > 15) errs.wa_number = 'Entre 10 y 15 dígitos (sin "+").';
  if (sp.length < 1 || sp.length > 200) errs.specialty = 'Entre 1 y 200 caracteres.';
  return errs;
}

function slugify(text) {
  const s = (text || '').toLowerCase().trim();
  const map = { á: 'a', é: 'e', í: 'i', ó: 'o', ú: 'u', ñ: 'n', ü: 'u' };
  return s.split('').map((c) => map[c] || c).join('')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/(^-|-$)/g, '');
}

function CopyButton({ value, label = 'Copiar', testid }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      onClick={() => {
        navigator.clipboard.writeText(value);
        setCopied(true);
        toast.success('Copiado');
        setTimeout(() => setCopied(false), 1500);
      }}
      data-testid={testid}
      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-indigo-50 text-indigo-700 border border-indigo-200 hover:bg-indigo-100 transition-colors"
    >
      {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
      {copied ? 'Copiado' : label}
    </button>
  );
}

function ChatPreview({ primary, secondary, text, businessName, welcome }) {
  const safeText = text || '#ffffff';
  const safePrimary = primary || '#1a1a2e';
  const safeSecondary = secondary || '#6366f1';
  const name = (businessName || 'Tu negocio').slice(0, 30);
  const initial = (name.charAt(0) || 'A').toUpperCase();
  const welcomeMsg = welcome || '¡Hola! ¿En qué te puedo ayudar?';

  return (
    <div
      data-testid="form-chat-preview"
      className="bg-gray-100 rounded-xl p-4 mt-3"
      style={{ fontFamily: '-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif' }}
    >
      <div className="text-[11px] text-gray-500 mb-2 font-medium uppercase tracking-wide">Vista previa en vivo</div>
      <div className="max-w-[320px] mx-auto rounded-xl overflow-hidden shadow-md bg-white">
        {/* Header */}
        <div
          className="flex items-center gap-2.5 px-3 py-2.5"
          style={{ background: safePrimary, color: safeText }}
        >
          <div
            className="w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm shrink-0"
            style={{ background: 'rgba(255,255,255,0.18)', color: safeText }}
          >
            {initial}
          </div>
          <div className="min-w-0">
            <div className="font-semibold text-sm leading-tight truncate" style={{ color: safeText }}>{name}</div>
            <div className="flex items-center gap-1.5 text-[10px]" style={{ color: safeText, opacity: 0.85 }}>
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
              <span>En línea</span>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="p-3 bg-slate-50 min-h-[170px] space-y-2">
          {/* Bot welcome bubble */}
          <div className="flex justify-start">
            <div className="max-w-[80%] rounded-2xl rounded-bl-sm bg-white border border-gray-200 px-3 py-2 text-[12.5px] leading-snug text-gray-900">
              {welcomeMsg}
            </div>
          </div>
          {/* User bubble */}
          <div className="flex justify-end">
            <div
              className="max-w-[80%] rounded-2xl rounded-br-sm px-3 py-2 text-[12.5px] leading-snug"
              style={{ background: safePrimary, color: safeText }}
            >
              ¿Dónde están ubicados?
            </div>
          </div>
          {/* Bot answer + WA cta */}
          <div className="flex justify-start">
            <div className="max-w-[88%] rounded-2xl rounded-bl-sm bg-white border border-gray-200 px-3 py-2 text-[12.5px] leading-snug text-gray-900">
              Estamos en{' '}
              <span style={{ color: safeSecondary, textDecoration: 'underline', fontWeight: 500 }}>
                Los Nísperos 840
              </span>
              .
              <div className="mt-2">
                <span
                  className="inline-flex items-center gap-1.5 text-white text-[11.5px] font-semibold rounded-full px-3 py-1.5"
                  style={{ background: '#22c55e', boxShadow: '0 4px 10px rgba(34,197,94,.3)' }}
                >
                  <svg viewBox="0 0 24 24" fill="currentColor" style={{ width: 12, height: 12 }}>
                    <path d="M20.52 3.48A11.78 11.78 0 0012.06 0C5.5 0 .15 5.34.15 11.91c0 2.1.55 4.15 1.6 5.96L0 24l6.3-1.65a11.93 11.93 0 005.75 1.46h.01c6.56 0 11.91-5.34 11.91-11.91 0-3.18-1.24-6.17-3.45-8.42z" />
                  </svg>
                  Hablar con ejecutivo
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Composer */}
        <div className="flex items-center gap-1.5 px-2 py-2 bg-white border-t border-gray-200">
          <div className="flex-1 rounded-full bg-slate-100 px-3 py-1.5 text-[11.5px] text-gray-400">
            Escribe tu mensaje…
          </div>
          <button
            type="button"
            disabled
            className="rounded-full px-3 py-1.5 text-[11.5px] font-semibold"
            style={{ background: safePrimary, color: safeText }}
          >
            Enviar
          </button>
        </div>

        {/* Footer */}
        <div className="text-center text-[9.5px] text-gray-400 py-1.5 bg-white">
          Powered by <span style={{ color: safeSecondary, fontWeight: 500 }}>ACBotHub</span>
        </div>
      </div>
    </div>
  );
}

function ClientForm({ initial, onCancel, onSave, saving, isEdit }) {
  const [form, setForm] = useState(() => ({
    ...EMPTY_FORM,
    ...(initial || {}),
    team_contacts: Array.isArray(initial?.team_contacts) ? initial.team_contacts : [],
  }));
  const [uploading, setUploading] = useState(false);
  // Per-row error map for the team contacts editor; populated when the
  // user tries to save with invalid rows.
  const [teamErrors, setTeamErrors] = useState({});
  const slugTouchedRef = React.useRef(Boolean(initial && initial.bot_slug));
  const fileInputRef = useRef(null);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // ----- Team contacts helpers -----
  const teamContacts = Array.isArray(form.team_contacts) ? form.team_contacts : [];
  const setTeamContacts = (next) => update('team_contacts', next);
  const addTeamContact = () => {
    if (teamContacts.length >= MAX_TEAM_CONTACTS) {
      toast.error(`Máximo ${MAX_TEAM_CONTACTS} contactos.`);
      return;
    }
    setTeamContacts([...teamContacts, { name: '', wa_number: '', specialty: '' }]);
  };
  const removeTeamContact = (idx) => {
    setTeamContacts(teamContacts.filter((_, i) => i !== idx));
    setTeamErrors((prev) => {
      const next = { ...prev };
      delete next[idx];
      // Re-key remaining errors to match new indices
      const reKeyed = {};
      Object.keys(next).forEach((k) => {
        const n = parseInt(k, 10);
        if (n > idx) reKeyed[n - 1] = next[k];
        else reKeyed[n] = next[k];
      });
      return reKeyed;
    });
  };
  const updateTeamContact = (idx, key, value) => {
    setTeamContacts(teamContacts.map((c, i) => (i === idx ? { ...c, [key]: value } : c)));
    // Clear that field's error live so the partner sees their fix immediately.
    setTeamErrors((prev) => {
      if (!prev[idx] || !prev[idx][key]) return prev;
      const rowCopy = { ...prev[idx] };
      delete rowCopy[key];
      const next = { ...prev };
      if (Object.keys(rowCopy).length === 0) delete next[idx];
      else next[idx] = rowCopy;
      return next;
    });
  };

  // Pre-submit validation for the team-contacts section. Returns true when
  // every row is valid; otherwise sets the inline-error state and surfaces
  // a toast so the partner notices where to fix.
  const validateTeam = () => {
    const errs = {};
    teamContacts.forEach((c, i) => {
      const e = validateTeamContact(c);
      if (Object.keys(e).length) errs[i] = e;
    });
    setTeamErrors(errs);
    if (Object.keys(errs).length === 0) return true;
    toast.error('Revisa los contactos del equipo: hay campos incompletos o inválidos.');
    return false;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateTeam()) return;
    // Normalize wa_numbers to digits only before sending
    const payload = {
      ...form,
      team_contacts: teamContacts.map((c) => ({
        name: (c.name || '').trim(),
        wa_number: (c.wa_number || '').replace(/\D/g, ''),
        specialty: (c.specialty || '').trim(),
      })),
    };
    onSave(payload);
  };

  // Upload a document to extract text and append it to the services field.
  // Works in both CREATE mode (stateless extract) and EDIT mode (with client_id).
  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const url = initial?.client_id
        ? `${BACKEND_URL}/api/partner/clients/${initial.client_id}/documents/upload`
        : `${BACKEND_URL}/api/partner/documents/extract`;
      const r = await axios.post(url, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const newText = (r.data?.extracted_text || '').trim();
      if (newText) {
        const existing = (form.services || '').trim();
        const merged = existing
          ? `${existing}\n\n--- ${file.name} ---\n${newText}`
          : `--- ${file.name} ---\n${newText}`;
        update('services', merged);
        toast.success(`Texto extraído de ${file.name} (${r.data.length} caracteres)`);
      } else {
        toast.error('No se pudo extraer texto del archivo.');
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Error al subir el archivo.';
      toast.error(detail);
    } finally {
      setUploading(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="bg-white border border-gray-200 rounded-xl p-6 mb-6 shadow-sm"
      data-testid="partner-client-form"
    >
      <div className="flex items-center justify-between mb-5">
        <h3 className="text-lg font-semibold text-gray-900">
          {isEdit ? 'Editar bot' : 'Crear nuevo bot'}
        </h3>
        <button type="button" onClick={onCancel} data-testid="form-cancel-btn"
          className="text-gray-400 hover:text-gray-600 p-1">
          <X className="w-5 h-5" />
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="biz">Nombre del negocio *</Label>
          <Input
            id="biz" required value={form.business_name}
            onChange={(e) => {
              const v = e.target.value;
              update('business_name', v);
              if (!isEdit && !slugTouchedRef.current) update('bot_slug', slugify(v));
            }}
            placeholder="Ej: Ichiban Sushi" data-testid="form-business-name"
          />
        </div>
        {!isEdit && (
          <div>
            <Label htmlFor="slug">Slug / URL del chat</Label>
            <div className="flex items-center gap-1">
              <span className="text-xs text-gray-500 whitespace-nowrap">acbothub.com/chat/</span>
              <Input
                id="slug" value={form.bot_slug}
                onChange={(e) => { slugTouchedRef.current = true; update('bot_slug', slugify(e.target.value)); }}
                placeholder="ichiban-sushi" data-testid="form-slug"
              />
            </div>
          </div>
        )}
        <div className="md:col-span-2">
          <Label htmlFor="addr">Dirección</Label>
          <Input id="addr" value={form.address} onChange={(e) => update('address', e.target.value)}
            placeholder="Los Nísperos 840, Los Ángeles" data-testid="form-address" />
        </div>
        <div className="md:col-span-2">
          <Label htmlFor="sched">Horarios de atención</Label>
          <Input id="sched" value={form.schedule} onChange={(e) => update('schedule', e.target.value)}
            placeholder="Lun-Sáb 13:00-23:00, Dom: Cerrado" data-testid="form-schedule" />
        </div>
        <div className="md:col-span-2">
          <div className="flex items-center justify-between mb-1">
            <Label htmlFor="services">Productos y servicios</Label>
            <>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.docx,.xlsx"
                onChange={handleUpload}
                style={{ display: 'none' }}
                data-testid="form-doc-upload-input"
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                data-testid="form-doc-upload-btn"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-indigo-50 text-indigo-700 border border-indigo-200 hover:bg-indigo-100 transition-colors disabled:opacity-50"
                title="Sube PDF, Word o Excel para extraer texto automáticamente"
              >
                {uploading
                  ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  : <Upload className="w-3.5 h-3.5" />}
                {uploading ? 'Procesando…' : 'Cargar documento (PDF/Word/Excel)'}
              </button>
            </>
          </div>
          <Textarea id="services" rows={5} value={form.services}
            onChange={(e) => update('services', e.target.value)}
            placeholder="Describe tu carta/catálogo, precios, promociones, etc."
            data-testid="form-services" />
          <p className="text-[11px] text-gray-500 mt-1">
            Tip: podés subir PDF, Word o Excel — el sistema extraerá el texto y lo agregará automáticamente.
          </p>
        </div>
        <div>
          <Label htmlFor="wa">WhatsApp del ejecutivo *</Label>
          <Input id="wa" value={form.wa_number}
            onChange={(e) => update('wa_number', e.target.value.replace(/[^\d+]/g, ''))}
            placeholder="56912345678" data-testid="form-wa" />
          <p className="text-[11px] text-gray-500 mt-1">Cuando el bot detecte interés, mostrará un botón verde que abre WhatsApp con este número.</p>
        </div>
        <div>
          <Label htmlFor="logo">URL del logo (opcional)</Label>
          <Input id="logo" value={form.logo_url} onChange={(e) => update('logo_url', e.target.value)}
            placeholder="https://..." data-testid="form-logo" />
        </div>

        {/* === Equipo de contactos (opcional) ===
            Sólo afecta al chat HTML del partner system. Si está vacío, el
            bot funciona exactamente igual que antes (sin triage). */}
        <div className="md:col-span-2 mt-2 pt-4 border-t border-gray-200" data-testid="form-team-contacts-section">
          <div className="flex items-start justify-between gap-3 mb-1">
            <div>
              <Label className="text-base">Equipo de contactos (opcional)</Label>
              <p className="text-xs text-gray-500 mt-1 max-w-xl">
                Si el negocio tiene varios ejecutivos especializados, agrégalos aquí. El bot
                preguntará al cliente qué necesita antes de derivarlo al contacto correcto.
                Si dejas esta sección vacía, el bot funciona igual que hoy.
              </p>
            </div>
            <span className="text-[11px] text-gray-400 whitespace-nowrap pt-1" data-testid="form-team-counter">
              {teamContacts.length} / {MAX_TEAM_CONTACTS}
            </span>
          </div>

          {teamContacts.length === 0 && (
            <div className="text-xs text-gray-400 italic py-2" data-testid="form-team-empty">
              Sin contactos agregados.
            </div>
          )}

          <div className="space-y-3 mt-2">
            {teamContacts.map((c, idx) => {
              const errs = teamErrors[idx] || {};
              return (
                <div
                  key={idx}
                  data-testid={`form-team-contact-${idx}`}
                  className="bg-gray-50 border border-gray-200 rounded-lg p-3 grid grid-cols-1 md:grid-cols-12 gap-2"
                >
                  <div className="md:col-span-3">
                    <Label className="text-[11px] text-gray-600">Nombre</Label>
                    <Input
                      value={c.name || ''}
                      onChange={(e) => updateTeamContact(idx, 'name', e.target.value.slice(0, 50))}
                      placeholder="Pedro"
                      data-testid={`form-team-name-${idx}`}
                      className={errs.name ? 'border-red-400 focus-visible:ring-red-400' : ''}
                    />
                    {errs.name && (
                      <p className="text-[11px] text-red-600 mt-0.5" data-testid={`form-team-name-error-${idx}`}>
                        {errs.name}
                      </p>
                    )}
                  </div>
                  <div className="md:col-span-3">
                    <Label className="text-[11px] text-gray-600">WhatsApp (solo dígitos)</Label>
                    <Input
                      value={c.wa_number || ''}
                      onChange={(e) => updateTeamContact(idx, 'wa_number', e.target.value.replace(/\D/g, '').slice(0, 15))}
                      placeholder="56998910543"
                      inputMode="numeric"
                      data-testid={`form-team-wa-${idx}`}
                      className={errs.wa_number ? 'border-red-400 focus-visible:ring-red-400' : ''}
                    />
                    {errs.wa_number && (
                      <p className="text-[11px] text-red-600 mt-0.5" data-testid={`form-team-wa-error-${idx}`}>
                        {errs.wa_number}
                      </p>
                    )}
                  </div>
                  <div className="md:col-span-5">
                    <Label className="text-[11px] text-gray-600">Especialidad</Label>
                    <Input
                      value={c.specialty || ''}
                      onChange={(e) => updateTeamContact(idx, 'specialty', e.target.value.slice(0, 200))}
                      placeholder="Vuelos internacionales, atención corporativa"
                      data-testid={`form-team-specialty-${idx}`}
                      className={errs.specialty ? 'border-red-400 focus-visible:ring-red-400' : ''}
                    />
                    {errs.specialty && (
                      <p className="text-[11px] text-red-600 mt-0.5" data-testid={`form-team-specialty-error-${idx}`}>
                        {errs.specialty}
                      </p>
                    )}
                  </div>
                  <div className="md:col-span-1 flex md:items-end md:justify-end">
                    <button
                      type="button"
                      onClick={() => removeTeamContact(idx)}
                      data-testid={`form-team-remove-${idx}`}
                      title="Eliminar contacto"
                      className="inline-flex items-center justify-center w-9 h-9 rounded-md text-red-500 hover:bg-red-50 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          <button
            type="button"
            onClick={addTeamContact}
            disabled={teamContacts.length >= MAX_TEAM_CONTACTS}
            data-testid="form-team-add-btn"
            className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-indigo-50 text-indigo-700 border border-indigo-200 hover:bg-indigo-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Plus className="w-3.5 h-3.5" />
            Agregar contacto
          </button>
        </div>
        {/* === fin Equipo de contactos === */}
        <div className="md:col-span-2">
          <Label>Colores corporativos</Label>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-1">
            <div>
              <div className="text-[11px] text-gray-500 mb-1">Primario</div>
              <div className="flex items-center gap-2">
                <input type="color" value={form.primary_color}
                  onChange={(e) => update('primary_color', e.target.value)}
                  className="w-10 h-10 rounded border border-gray-300 cursor-pointer shrink-0"
                  data-testid="form-primary-color-picker" />
                <Input value={form.primary_color}
                  onChange={(e) => update('primary_color', e.target.value)}
                  className="flex-1" data-testid="form-primary-color" />
              </div>
            </div>
            <div>
              <div className="text-[11px] text-gray-500 mb-1">Secundario</div>
              <div className="flex items-center gap-2">
                <input type="color" value={form.secondary_color}
                  onChange={(e) => update('secondary_color', e.target.value)}
                  className="w-10 h-10 rounded border border-gray-300 cursor-pointer shrink-0"
                  data-testid="form-secondary-color-picker" />
                <Input value={form.secondary_color}
                  onChange={(e) => update('secondary_color', e.target.value)}
                  className="flex-1" data-testid="form-secondary-color" />
              </div>
            </div>
            <div>
              <div className="text-[11px] text-gray-500 mb-1">Texto</div>
              <div className="flex items-center gap-2">
                <input type="color" value={form.text_color}
                  onChange={(e) => update('text_color', e.target.value)}
                  className="w-10 h-10 rounded border border-gray-300 cursor-pointer shrink-0"
                  data-testid="form-text-color-picker" />
                <Input value={form.text_color}
                  onChange={(e) => update('text_color', e.target.value)}
                  className="flex-1" data-testid="form-text-color" />
              </div>
            </div>
          </div>
          <ChatPreview
            primary={form.primary_color}
            secondary={form.secondary_color}
            text={form.text_color}
            businessName={form.business_name}
            welcome={form.welcome_message}
          />
        </div>
        <div className="md:col-span-2">
          <Label htmlFor="welcome">Mensaje de bienvenida</Label>
          <Input id="welcome" value={form.welcome_message}
            onChange={(e) => update('welcome_message', e.target.value)}
            placeholder="¡Hola! ¿En qué te puedo ayudar?" data-testid="form-welcome" />
        </div>
      </div>
      <div className="flex items-center justify-end gap-2 mt-6 pt-4 border-t border-gray-100">
        <Button type="button" variant="outline" onClick={onCancel} data-testid="form-cancel-btn-2">
          Cancelar
        </Button>
        <Button type="submit" disabled={saving || !form.business_name.trim()} data-testid="form-save-btn"
          className="bg-indigo-600 hover:bg-indigo-700 text-white">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
          {isEdit ? 'Guardar cambios' : 'Crear bot'}
        </Button>
      </div>
    </form>
  );
}

function ApiKeyPanel({ keyInfo, onSave, onDelete }) {
  const [provider, setProvider] = useState(keyInfo?.ai_provider || 'openai');
  const [apiKey, setApiKey] = useState('');
  const [showKey, setShowKey] = useState(false);
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(false);
  const hasKey = !!keyInfo?.has_api_key;

  const handleSave = async (e) => {
    e.preventDefault();
    if (!apiKey.trim()) return;
    setSaving(true);
    await onSave(apiKey.trim(), provider);
    setApiKey('');
    setSaving(false);
  };

  const handleRemove = async () => {
    if (!window.confirm('¿Quitar tu API Key? Los bots dejarán de funcionar hasta que cargues una nueva.')) return;
    setRemoving(true);
    await onDelete();
    setRemoving(false);
  };

  return (
    <div
      className={`border rounded-xl p-5 mb-6 shadow-sm ${hasKey ? 'bg-white border-gray-200' : 'bg-amber-50/60 border-amber-300'}`}
      data-testid="partner-api-key-panel"
    >
      <div className="flex items-start gap-3 mb-4">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${hasKey ? 'bg-indigo-50 text-indigo-600' : 'bg-amber-100 text-amber-700'}`}>
          {hasKey ? <KeyRound className="w-5 h-5" /> : <AlertTriangle className="w-5 h-5" />}
        </div>
        <div className="flex-1">
          <h3 className="font-semibold text-gray-900">
            {hasKey ? 'Tu API Key de IA está configurada' : 'Configurá tu API Key de IA'}
          </h3>
          <p className="text-sm text-gray-600 mt-0.5">
            {hasKey
              ? `Proveedor: ${keyInfo.ai_provider === 'anthropic' ? 'Anthropic (Claude)' : 'OpenAI'} · ${keyInfo.api_key_masked || '****'}`
              : 'Sin una API Key configurada, tus bots no podrán responder a los clientes. Tu key se guarda encriptada y solo se usa para tus conversaciones.'}
          </p>
        </div>
        {hasKey && (
          <button
            type="button"
            onClick={handleRemove}
            disabled={removing}
            className="text-xs text-red-600 hover:bg-red-50 px-3 py-1.5 rounded disabled:opacity-50"
            data-testid="delete-api-key-btn"
          >
            {removing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Quitar key'}
          </button>
        )}
      </div>
      <form onSubmit={handleSave} className="grid grid-cols-1 sm:grid-cols-12 gap-2">
        <div className="sm:col-span-3">
          <select
            value={provider}
            onChange={(e) => setProvider(e.target.value)}
            className="w-full h-10 px-3 border border-gray-300 rounded-md text-sm bg-white"
            data-testid="api-key-provider"
          >
            <option value="openai">OpenAI (GPT)</option>
            <option value="anthropic">Anthropic (Claude)</option>
          </select>
        </div>
        <div className="sm:col-span-7">
          <div className="relative">
            <Input
              type={showKey ? 'text' : 'password'}
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder={provider === 'anthropic' ? 'sk-ant-...' : 'sk-...'}
              data-testid="api-key-input"
              className="pr-9 font-mono text-sm"
            />
            <button type="button" onClick={() => setShowKey((s) => !s)}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1"
              aria-label="Mostrar/ocultar key">
              {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>
        <div className="sm:col-span-2">
          <Button
            type="submit"
            disabled={saving || !apiKey.trim()}
            data-testid="save-api-key-btn"
            className="w-full bg-indigo-600 hover:bg-indigo-700 text-white"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (hasKey ? 'Reemplazar' : 'Guardar')}
          </Button>
        </div>
      </form>
    </div>
  );
}

export default function PartnerDashboardPage() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [clients, setClients] = useState([]);
  const [stats, setStats] = useState({});
  const [keyInfo, setKeyInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadClients = useCallback(async () => {
    try {
      const [clientsRes, statsRes, meRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/partner/clients`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/partner/stats/all`, { withCredentials: true }).catch(() => ({ data: { stats: {} } })),
        axios.get(`${BACKEND_URL}/api/partner/me`, { withCredentials: true }).catch(() => ({ data: null })),
      ]);
      setClients(clientsRes.data.clients || []);
      setStats(statsRes.data?.stats || {});
      setKeyInfo(meRes.data || null);
    } catch (e) {
      toast.error('Error al cargar tus clientes');
    } finally {
      setLoading(false);
    }
  }, []);

  const saveApiKey = async (apiKey, provider) => {
    try {
      await axios.post(`${BACKEND_URL}/api/partner/api-key`,
        { api_key: apiKey, ai_provider: provider }, { withCredentials: true });
      toast.success('API Key guardada correctamente');
      await loadClients();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al guardar la API Key');
    }
  };

  const deleteApiKey = async () => {
    try {
      await axios.delete(`${BACKEND_URL}/api/partner/api-key`, { withCredentials: true });
      toast.success('API Key eliminada');
      await loadClients();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al eliminar la API Key');
    }
  };

  const [formMode, setFormMode] = useState(null); // null | 'create' | client_id
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);

  useEffect(() => { loadClients(); }, [loadClients]);

  const handleSave = async (form) => {
    setSaving(true);
    try {
      if (formMode === 'create') {
        await axios.post(`${BACKEND_URL}/api/partner/clients/create`, form, { withCredentials: true });
        toast.success('Bot creado correctamente');
      } else if (editing) {
        await axios.patch(
          `${BACKEND_URL}/api/partner/clients/${editing.client_id}`,
          form, { withCredentials: true }
        );
        toast.success('Bot actualizado');
      }
      setFormMode(null);
      setEditing(null);
      await loadClients();
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Error al guardar';
      toast.error(detail);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (client) => {
    if (!window.confirm(`¿Eliminar el bot "${client.business_name}"? Esta acción no se puede deshacer.`)) return;
    setDeleting(client.client_id);
    try {
      await axios.delete(`${BACKEND_URL}/api/partner/clients/${client.client_id}`, { withCredentials: true });
      toast.success('Bot eliminado');
      await loadClients();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al eliminar');
    } finally {
      setDeleting(null);
    }
  };

  const buildChatUrl = (slug) => {
    const base = (typeof window !== 'undefined' && window.location.origin) ? window.location.origin : 'https://acbothub.com';
    return `${base}/chat/${slug}`;
  };
  const buildEmbedCode = (clientId) => {
    const base = (typeof window !== 'undefined' && window.location.origin) ? window.location.origin : 'https://acbothub.com';
    return `<script src="${base}/widget.js" data-bot="${clientId}" async></script>`;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <nav className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-indigo-600 to-purple-600 flex items-center justify-center">
              <span className="text-white font-bold text-sm">A</span>
            </div>
            <div>
              <div className="font-bold text-gray-900">ACBotHub</div>
              <div className="text-[11px] text-gray-500">Panel de Partner</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-600 hidden sm:block">{user?.email}</span>
            <Button variant="outline" size="sm" onClick={logout} data-testid="partner-logout-btn">
              <LogOut className="w-4 h-4 mr-1.5" /> Salir
            </Button>
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-gray-950">Mis Clientes</h1>
            <p className="text-gray-600 mt-1 text-sm">
              Crea bots de chat HTML para tus clientes. Cada bot incluye un link móvil y un código embebible.
            </p>
          </div>
          {formMode === null && (
            <Button
              onClick={() => { setEditing(null); setFormMode('create'); }}
              data-testid="partner-new-client-btn"
              className="bg-indigo-600 hover:bg-indigo-700 text-white shadow-md"
            >
              <Plus className="w-4 h-4 mr-1.5" /> Nuevo bot
            </Button>
          )}
        </div>

        {formMode === null && keyInfo && (
          <ApiKeyPanel keyInfo={keyInfo} onSave={saveApiKey} onDelete={deleteApiKey} />
        )}

        {formMode !== null && (
          <ClientForm
            key={editing?.client_id || 'create-new'}
            initial={editing}
            isEdit={Boolean(editing)}
            saving={saving}
            onCancel={() => { setFormMode(null); setEditing(null); }}
            onSave={handleSave}
          />
        )}

        {loading ? (
          <div className="text-center py-20 text-gray-400">
            <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
            Cargando…
          </div>
        ) : clients.length === 0 && formMode === null ? (
          <div className="bg-white border border-dashed border-gray-300 rounded-xl p-12 text-center">
            <div className="w-14 h-14 rounded-full bg-indigo-50 flex items-center justify-center mx-auto mb-3">
              <Plus className="w-6 h-6 text-indigo-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-1">Aún no tienes clientes</h3>
            <p className="text-sm text-gray-500 mb-4">Crea tu primer bot para empezar a ofrecer asistentes a tus clientes.</p>
            <Button onClick={() => setFormMode('create')} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="partner-empty-create-btn">
              <Plus className="w-4 h-4 mr-1.5" /> Crear mi primer bot
            </Button>
          </div>
        ) : (
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm" data-testid="partner-clients-table">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr className="text-left">
                    <th className="px-4 py-3 font-semibold text-gray-700">Negocio</th>
                    <th className="px-4 py-3 font-semibold text-gray-700">Leads (30d)</th>
                    <th className="px-4 py-3 font-semibold text-gray-700">Link del chat</th>
                    <th className="px-4 py-3 font-semibold text-gray-700">Código embed</th>
                    <th className="px-4 py-3 font-semibold text-gray-700">Creado</th>
                    <th className="px-4 py-3 font-semibold text-gray-700 text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {clients.map((c) => {
                    const url = buildChatUrl(c.bot_slug);
                    const embed = buildEmbedCode(c.client_id);
                    const cStats = stats[c.client_id] || { conversations_30d: 0, wa_clicks_30d: 0 };
                    return (
                      <tr key={c.client_id} className="border-b border-gray-100 hover:bg-gray-50/60"
                        data-testid={`client-row-${c.client_id}`}>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2.5">
                            <div className="w-9 h-9 rounded-full flex items-center justify-center font-semibold text-white text-sm shrink-0"
                              style={{ background: c.primary_color || '#1a1a2e' }}>
                              {(c.business_name || 'A').charAt(0).toUpperCase()}
                            </div>
                            <div>
                              <div className="font-medium text-gray-900">{c.business_name}</div>
                              {c.wa_number && (
                                <div className="text-[11px] text-gray-500">WA: +{c.wa_number}</div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3" data-testid={`stats-${c.client_id}`}>
                            <div
                              className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-indigo-50 text-indigo-700 text-xs font-medium"
                              title="Conversaciones únicas en los últimos 30 días"
                              data-testid={`stats-conv-${c.client_id}`}
                            >
                              <MessageCircle className="w-3.5 h-3.5" />
                              {cStats.conversations_30d}
                            </div>
                            <div
                              className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-emerald-50 text-emerald-700 text-xs font-medium"
                              title="Clientes que tocaron el botón WhatsApp en los últimos 30 días"
                              data-testid={`stats-wa-${c.client_id}`}
                            >
                              <MousePointerClick className="w-3.5 h-3.5" />
                              {cStats.wa_clicks_30d}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <a href={url} target="_blank" rel="noopener noreferrer"
                              className="text-indigo-600 hover:underline text-xs font-mono inline-flex items-center gap-1"
                              data-testid={`open-chat-${c.client_id}`}>
                              /chat/{c.bot_slug}
                              <ExternalLink className="w-3 h-3" />
                            </a>
                            <CopyButton value={url} label="Copiar" testid={`copy-link-${c.client_id}`} />
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <CopyButton value={embed} label="Copiar código" testid={`copy-embed-${c.client_id}`} />
                        </td>
                        <td className="px-4 py-3 text-gray-500 text-xs">
                          {c.created_at ? new Date(c.created_at).toLocaleDateString() : '-'}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="inline-flex items-center gap-1">
                            <button
                              onClick={() => { setEditing(c); setFormMode(c.client_id); }}
                              className="p-1.5 text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 rounded transition-colors"
                              data-testid={`edit-client-${c.client_id}`}
                              title="Editar"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDelete(c)}
                              disabled={deleting === c.client_id}
                              className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
                              data-testid={`delete-client-${c.client_id}`}
                              title="Eliminar"
                            >
                              {deleting === c.client_id
                                ? <Loader2 className="w-4 h-4 animate-spin" />
                                : <Trash2 className="w-4 h-4" />}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
